import { ChangeDetectionStrategy, Component, computed, effect, input, signal } from '@angular/core';

import { Dynamic } from '@shared/registry/dynamic';

import { type SectionedExpandableCardConfig } from './sectioned-expandable-card.types';

@Component({
  selector: 'app-sectioned-expandable-card',
  templateUrl: './sectioned-expandable-card.html',
  imports: [Dynamic],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SectionedExpandableCard {
  readonly config = input.required<SectionedExpandableCardConfig>();

  protected readonly isExpanded = signal(false);

  private hasInitialized = false;

  constructor() {
    effect(() => {
      const cfg = this.config();
      if (!this.hasInitialized) {
        this.hasInitialized = true;
        if (cfg.initialExpanded) {
          this.isExpanded.set(true);
        }
      }
    });
  }

  protected readonly displayedSections = computed(() => {
    const sections = this.config().sections;
    return this.isExpanded() ? sections : sections.slice(0, 1);
  });

  protected readonly containerBackground = computed(() => {
    if (this.config().variant === 'highlighted') {
      return 'linear-gradient(135deg, var(--gradients-tertiary-start), var(--gradients-tertiary-middle), var(--gradients-tertiary-stop))';
    }
    return 'var(--sectionedexpandablecard-idle-color-background)';
  });

  protected readonly borderStyle = computed(() => {
    const cfg = this.config();
    if (cfg.variant === 'idle' || cfg.border) {
      return 'var(--sectionedexpandablecard-border-width) solid var(--sectionedexpandablecard-idle-color-border)';
    }
    return 'none';
  });

  protected readonly sectionTextPropsMap = computed(() =>
    this.displayedSections().map((section) => ({
      config: {
        text: section.text,
        size: 'base' as const,
        fontWeight: 'semibold' as const,
        fontFamily: 'var(--sectionedexpandablecard-font-family, var(--font-sans))',
        color: 'var(--sectionedexpandablecard-content-color)',
      },
    })),
  );

  protected readonly buttonProps = computed(() => ({
    config: this.isExpanded() ? this.config().collapseButton : this.config().expandButton,
  }));

  protected toggle(): void {
    this.isExpanded.update((v) => !v);
  }
}
